import type React from 'react'
import styles from './StationCardLoading.module.css'

/**
 * Loading skeleton — mirrors the Normal card silhouette while the
 * station record is being fetched.
 *
 * Spec: `metro/docs/superpowers/specs/2026-05-20-stationcard-v2-figma-pixelperfect.md`
 * §2.3. Shimmer animation is runtime (CSS `@keyframes shimmer-pulse`,
 * 0.8s ease-in-out infinite alternate, opacity 1 ↔ 0.55 per lead
 * 2026-05-20). Figma renders the static end-frame; snapshots lock the
 * static DOM only — temporal flakiness is avoided by snapshotting
 * structure, not pixel intensity.
 *
 * Element fills are all `--mm-color-background-secondary`, matching
 * Figma's `bg/secondary` skeleton fill. Skeleton corner radii are 16
 * (NOT 18 / 32 like the live PrimaryButton / SecondaryButton — see
 * Figma drift §B.4).
 */
export function StationCardLoading({
  loadingLabel = 'Loading station details',
}: {
  /** Accessible label. @defaultValue `'Loading station details'` */
  loadingLabel?: string
} = {}): React.ReactElement {
  return (
    <article
      className={styles.card}
      data-testid="station-card-loading"
      aria-busy="true"
      aria-label={loadingLabel}
    >
      <div className={styles.header}>
        <span className={styles.lineCircle} aria-hidden="true" />
        <div className={styles.titlesColumn}>
          <span className={styles.titleBar} aria-hidden="true" />
          <span className={styles.subtitleBar} aria-hidden="true" />
        </div>
        <span className={styles.zoneBadge} aria-hidden="true" />
      </div>
      <div className={styles.actions}>
        <div className={styles.actionRow}>
          <span className={styles.fromToRect} aria-hidden="true" />
          <span className={styles.fromToRect} aria-hidden="true" />
        </div>
        <span className={styles.infoRect} aria-hidden="true" />
      </div>
    </article>
  )
}
